import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../services/api';
import EditPayrollModal from '../components/EditPayrollModal';
import { useAuth } from '../contexts/AuthContext';

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;

const monthOptions = Array.from({ length: 12 }, (_, idx) => idx + 1);

const formatNumber = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString('ko-KR');
};

const tableContainer = {
  marginTop: '20px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'hidden',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const thStyle = {
  background: '#f2f4f6',
  borderBottom: '1px solid #d0d7de',
  padding: '10px',
  textAlign: 'center',
  fontWeight: 600,
};

const tdStyle = {
  borderBottom: '1px solid #eef2f6',
  padding: '10px',
  textAlign: 'center',
};

const filterGroup = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '16px',
};

const labelStyle = { fontWeight: 600 };
const inputStyle = {
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid #d0d7de',
};

const buttonStyle = {
  padding: '10px 16px',
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const secondaryButton = {
  ...buttonStyle,
  background: '#475569',
};

const infoBox = {
  marginTop: '16px',
  padding: '12px 16px',
  background: '#f8fafc',
  border: '1px solid #cbd5f5',
  borderRadius: '8px',
  fontSize: '0.9rem',
  lineHeight: 1.5,
};

function PayrollRegisterPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [nameKeyword, setNameKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPassword, setExcelPassword] = useState('');
  const [resultMessage, setResultMessage] = useState(null);
  const [errors, setErrors] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [importInProgress, setImportInProgress] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(null);

  const excelFileInputRef = useRef(null);
  const clearExcelFileSelection = useCallback(() => {
    setExcelFile(null);
    if (excelFileInputRef.current) {
      excelFileInputRef.current.value = '';
    }
  }, []);

  const fetchProjects = async (targetYear = year) => {
    try {
      const response = await api.get('/projects', { params: { year: targetYear } });
      setProjects(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPayrolls = async (overrides = {}) => {
    const effectiveProjectId = overrides.projectId ?? projectId;
    setLoading(true);
    try {
      const response = await api.get('/payroll', {
        params: {
          year,
          month,
          includeZero: true,
          projectId: effectiveProjectId || undefined,
          name: nameKeyword || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        },
      });
      setPayrolls(response.data || []);
      setSelectedIds([]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(year);
  }, [year]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = () => {
    fetchPayrolls();
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      const text = '엑셀 파일을 선택해주세요.';
      setResultMessage({ type: 'error', text });
      setFeedbackModal({ type: 'error', message: text });
      return;
    }
    if (!projectId) {
      const text =
        '엑셀 일괄등록 전에 위 검색탭에서 연도·월·사업단을 모두 선택해 주세요. (사업단은 전체가 아닌 특정 사업을 선택해야 합니다.)';
      setResultMessage({ type: 'error', text });
      setFeedbackModal({ type: 'error', message: text });
      return;
    }
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    formData.append('year', year);
    formData.append('month', month);
    formData.append('projectId', projectId);
    if (excelPassword) {
      formData.append('password', excelPassword);
    }

    setImportInProgress(true);
    try {
      const response = await api.post('/payroll/import', formData);
      const successText = `총 ${response.data.importedCount}건이 등록되었고 ${response.data.errorCount}건이 제외되었습니다.`;
      setResultMessage({ type: 'success', text: successText });
      setErrors(response.data.errors || []);
      setFeedbackModal({ type: 'success', message: successText });
      fetchPayrolls();
    } catch (error) {
      const message = error.response?.data?.message || '업로드 중 오류가 발생했습니다.';
      setResultMessage({ type: 'error', text: message });
      setFeedbackModal({ type: 'error', message });
    } finally {
      setImportInProgress(false);
      setExcelPassword('');
      clearExcelFileSelection();
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === payrolls.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(payrolls.map((item) => item.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      setResultMessage({ type: 'error', text: '삭제할 항목을 선택해주세요.' });
      return;
    }
    if (!window.confirm('선택한 급여 데이터를 삭제하시겠습니까?')) {
      return;
    }
    try {
      setActionLoading(true);
      await api.post('/payroll/bulk-delete', { payrollIds: selectedIds });
      setResultMessage({ type: 'success', text: `${selectedIds.length}건의 급여를 삭제했습니다.` });
      setSelectedIds([]);
      fetchPayrolls();
    } catch (error) {
      const message = error.response?.data?.message || '삭제 중 오류가 발생했습니다.';
      setResultMessage({ type: 'error', text: message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveEdit = async (formData) => {
    if (!editingPayroll) return;
    try {
      setActionLoading(true);
      await api.put(`/payroll/${editingPayroll.id}`, formData);
      setResultMessage({ type: 'success', text: '급여 정보를 수정했습니다.' });
      setEditingPayroll(null);
      fetchPayrolls();
    } catch (error) {
      const message = error.response?.data?.message || '수정 중 오류가 발생했습니다.';
      setResultMessage({ type: 'error', text: message });
    } finally {
      setActionLoading(false);
    }
  };

  const totalGross = payrolls.reduce((sum, item) => sum + Number(item.grossPay || 0), 0);
  const totalNet = payrolls.reduce((sum, item) => sum + Number(item.netPay || 0), 0);
  const isAllSelected = payrolls.length > 0 && selectedIds.length === payrolls.length;

  const statusLabels = {
    draft: '임시저장',
    ready: '발송준비',
    queued: '발송대기',
    processing: '발송중',
    sent: '발송완료',
    success: '발송성공',
    failed: '발송실패',
  };

  const renderStatus = (value) => statusLabels[value] || value || '-';

  const stickyBarStyle = {
    position: isMobile ? 'static' : 'sticky',
    top: isMobile ? 'auto' : 60,
    zIndex: isMobile ? 'auto' : 5,
    background: '#ffffff',
    paddingBottom: 8,
    borderBottom: '1px solid #e5e7eb',
  };

  return (
    <div>
      <div style={stickyBarStyle}>
        <h2 style={{ marginBottom: '6px' }}>참여자 급여 등록</h2>
        <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '10px' }}>
          급여 항목(지급·공제 금액)을 수정하면 상태가 자동으로 <strong>발송준비</strong>로 변경되며,
          이후 명세표 보기 또는 발송 시 최신 내용으로 PDF가 자동 재생성됩니다.
        </div>

        <div style={filterGroup}>
          <label style={labelStyle}>
            연도
            <input
              type="number"
              value={year}
              min="2000"
              style={{ ...inputStyle, marginLeft: '6px', width: '110px' }}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </label>

          <label style={labelStyle}>
            월
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              style={{ ...inputStyle, marginLeft: '6px' }}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {m}월
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            사업단
            <select
              value={projectId}
              onChange={(e) => {
                const nextProjectId = e.target.value;
                setProjectId(nextProjectId);
                fetchPayrolls({ projectId: nextProjectId });
              }}
              style={{ ...inputStyle, marginLeft: '6px' }}
            >
              <option value="">전체</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.projectName}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            성명
            <input
              type="text"
              value={nameKeyword}
              style={{ ...inputStyle, marginLeft: '6px' }}
              placeholder="성명 검색"
              onChange={(e) => setNameKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </label>

          <label style={labelStyle}>
            상태
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ ...inputStyle, marginLeft: '6px' }}
            >
              <option value="all">전체</option>
              <option value="draft">임시저장</option>
              <option value="ready">발송준비</option>
              <option value="queued">발송대기</option>
              <option value="sent">발송완료</option>
              <option value="failed">발송실패</option>
            </select>
          </label>

          <button style={secondaryButton} onClick={handleSearch} disabled={loading}>
            조회
          </button>
        </div>

        {isAdmin && (
          <div
            style={{
              padding: '15px',
              marginTop: '8px',
              marginBottom: '12px',
              backgroundColor: '#e3f2fd',
              border: '2px solid #2196f3',
              borderRadius: '5px',
              fontSize: '14px',
              lineHeight: '1.6',
              fontWeight: '500',
            }}
          >
            <div style={{ color: '#1976d2', fontWeight: 'bold', marginBottom: '8px', fontSize: '15px' }}>
              💡 엑셀 파일 준비 방법
            </div>
            <div style={{ color: '#1565c0' }}>
              노인일자리 정보시스템의 <strong>[사업운영 → 보수관리]</strong>에서
              <strong> 해당월 급여자료를 출력</strong>한 엑셀 데이터를 그대로 사용하시면 됩니다.
              <br />
              <span style={{ color: '#d32f2f', fontWeight: '600' }}>
                계좌와 이메일 정보는 마지막 달에 입력한 데이터로 자동 갱신됩니다.
              </span>
              <br />
              <span style={{ color: '#b45309', fontWeight: '600' }}>
                엑셀 불러오기는 위에서 연도·월·<strong>사업단(전체 제외)</strong>을 선택한 뒤 진행하세요. 급여
                사업단은 선택한 사업으로 저장되며, 명단은 근무관리와 같이 해당 사업·해당 월 참여 조건에 맞는 참여자만
                매칭됩니다.
              </span>
            </div>
          </div>
        )}

        <div style={{ ...filterGroup, marginTop: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
          {isAdmin ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
              <label style={labelStyle}>
                엑셀 업로드
                <input
                  ref={excelFileInputRef}
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                  style={{ marginLeft: '6px' }}
                />
              </label>
              <label style={labelStyle}>
                엑셀 비밀번호 (있는 경우만)
                <input
                  type="password"
                  value={excelPassword}
                  onChange={(e) => setExcelPassword(e.target.value)}
                  placeholder="비밀번호 없는 엑셀은 비워두세요"
                  autoComplete="new-password"
                  style={{ marginLeft: '6px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4 }}
                />
              </label>
              <button
                style={buttonStyle}
                onClick={handleExcelUpload}
                disabled={loading || actionLoading || importInProgress}
              >
                엑셀 불러오기
              </button>
            </div>
          ) : (
            <div />
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{ ...secondaryButton, background: '#dc2626' }}
              onClick={handleDeleteSelected}
              disabled={loading || actionLoading || selectedIds.length === 0}
            >
              선택 삭제
            </button>
          </div>
        </div>
      </div>

      {resultMessage && (
        <div
          style={{
            ...infoBox,
            borderColor: resultMessage.type === 'error' ? '#fca5a5' : '#93c5fd',
            background: resultMessage.type === 'error' ? '#fef2f2' : '#eff6ff',
            color: resultMessage.type === 'error' ? '#b91c1c' : '#1d4ed8',
          }}
        >
          {resultMessage.text}
        </div>
      )}

      {errors.length > 0 && (
        <div style={{ ...infoBox, borderColor: '#fca5a5', background: '#fef2f2', color: '#b91c1c' }}>
          <strong>처리되지 않은 항목</strong>
          <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
            {errors.map((item, idx) => (
              <li key={idx}>
                {item.row?.name || '성명 미상'} / {item.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={infoBox}>
        <div style={{ marginBottom: '8px', fontWeight: 600 }}>조회 인원: {payrolls.length}명</div>
        <div>총 지급합계: {formatNumber(totalGross)}원</div>
        <div>총 차감지급액: {formatNumber(totalNet)}원</div>
        <div style={{ marginTop: '8px', color: '#64748b' }}>
          {isAdmin ? (
            <>
              • 엑셀 일괄등록 시 위에서 선택한 <strong>연도·월·사업단</strong>에 해당하고, 성명·주민번호 앞 6자리가
              일치하는 참여자만 반영됩니다.
              <br />• 근무월(엑셀)이 선택한 연·월과 다르면 제외되며, 은행/계좌·이메일은 참여자 명단에도 즉시
              갱신됩니다.
            </>
          ) : (
            <>
              • 일반 사용자는 <strong>조회만</strong> 가능합니다. 급여 수정·선택 삭제·엑셀 일괄 불러오기는 관리자만 사용할 수
              있습니다. 검증이 모두 통과한 뒤 급여 반영은 <strong>급여 검증 및 입력</strong> 화면에서 진행할 수 있습니다.
            </>
          )}
        </div>
      </div>

      <div style={tableContainer}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {isAdmin && (
                <th style={thStyle}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    disabled={payrolls.length === 0}
                  />
                </th>
              )}
              <th style={thStyle}>성명</th>
              <th style={thStyle}>사업단</th>
              <th style={thStyle}>기본급</th>
              <th style={thStyle}>주휴수당</th>
              <th style={thStyle}>시간외수당</th>
              <th style={thStyle}>연차수당</th>
              <th style={thStyle}>기타수당</th>
              <th style={thStyle}>건강보험</th>
              <th style={thStyle}>요양보험</th>
              <th style={thStyle}>고용보험</th>
              <th style={thStyle}>산재보험</th>
              <th style={thStyle}>소득세</th>
              <th style={thStyle}>지방세</th>
              <th style={thStyle}>차감지급액</th>
              <th style={thStyle}>상태</th>
              {isAdmin && <th style={thStyle}>작업</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={tdStyle} colSpan={isAdmin ? 17 : 16}>
                  불러오는 중...
                </td>
              </tr>
            ) : payrolls.length === 0 ? (
              <tr>
                <td style={tdStyle} colSpan={isAdmin ? 17 : 16}>
                  데이터가 없습니다.
                </td>
              </tr>
            ) : (
              payrolls.map((item) => (
                <tr key={item.id}>
                  {isAdmin && (
                    <td style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleSelectOne(item.id)}
                      />
                    </td>
                  )}
                  <td style={tdStyle}>{item.Participant?.name}</td>
                  <td style={tdStyle}>{item.Project?.projectName || '-'}</td>
                  <td style={tdStyle}>{formatNumber(item.basePay)}</td>
                  <td style={tdStyle}>{formatNumber(item.weeklyAllowance)}</td>
                  <td style={tdStyle}>{formatNumber(item.overtimeAllowance)}</td>
                  <td style={tdStyle}>{formatNumber(item.annualAllowance)}</td>
                  <td style={tdStyle}>{formatNumber(item.otherAllowance)}</td>
                  <td style={tdStyle}>{formatNumber(item.healthInsurance)}</td>
                  <td style={tdStyle}>{formatNumber(item.longTermCareInsurance)}</td>
                  <td style={tdStyle}>{formatNumber(item.employmentInsurance)}</td>
                  <td style={tdStyle}>{formatNumber(item.industrialAccidentInsurance)}</td>
                  <td style={tdStyle}>{formatNumber(item.incomeTax)}</td>
                  <td style={tdStyle}>{formatNumber(item.localTax)}</td>
                  <td style={tdStyle}>{formatNumber(item.netPay)}</td>
                  <td style={tdStyle}>{renderStatus(item.status)}</td>
                  {isAdmin && (
                    <td style={tdStyle}>
                      <button
                        style={{ ...secondaryButton, padding: '6px 10px' }}
                        onClick={() => setEditingPayroll(item)}
                        disabled={actionLoading}
                      >
                        수정
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isAdmin && (
        <EditPayrollModal
          isOpen={Boolean(editingPayroll)}
          payroll={editingPayroll}
          onClose={() => setEditingPayroll(null)}
          onSubmit={handleSaveEdit}
          submitting={actionLoading}
        />
      )}

      {importInProgress && (
        <div
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-label="엑셀 등록 진행 중"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '28px 32px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>
              엑셀 급여 등록 중
            </div>
            <div style={{ fontSize: '0.95rem', color: '#475569', lineHeight: 1.55 }}>
              서버에 파일을 보내고 급여를 반영하고 있습니다.
              <br />
              창을 닫지 말고 잠시만 기다려 주세요.
            </div>
            <div style={{ marginTop: '20px', fontSize: '0.85rem', color: '#94a3b8' }}>처리 중…</div>
          </div>
        </div>
      )}

      {feedbackModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-feedback-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setFeedbackModal(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px 28px',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              id="payroll-feedback-title"
              style={{
                fontSize: '1.05rem',
                fontWeight: 700,
                marginBottom: '12px',
                color: feedbackModal.type === 'error' ? '#b91c1c' : '#1d4ed8',
              }}
            >
              {feedbackModal.type === 'success' ? '등록 완료' : '알림'}
            </div>
            <div style={{ fontSize: '0.95rem', color: '#334155', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {feedbackModal.message}
            </div>
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button type="button" style={buttonStyle} onClick={() => setFeedbackModal(null)}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollRegisterPage;

